import React, { useEffect, useState } from "react";
import ApiClient from "../services/fuelandmoreapi";
import type { TClientFleet } from "@/types";
import VesselList from "./VesselList";
import AddFleet from "./AddFleet";
import { config } from "../config";
import BinIcon from "./BinIcon";
import "./FleetList.css";

interface FleetListProps {
  clientDomain: string;
}

const FleetList: React.FC<FleetListProps> = ({ clientDomain }) => {
  const [fleets, setFleets] = useState<TClientFleet[]>([]);
  const [selectedFleet, setSelectedFleet] = useState<TClientFleet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { apiBaseUrl } = config;
  const apiUrl = apiBaseUrl;

  useEffect(() => {
    const fetchFleets = async () => {
      try {
        const apiClient = new ApiClient(apiUrl);
        const response = await apiClient.getClientFleets(clientDomain);
        setFleets(response.data);
      } catch (err) {
        console.error("Error fetching fleets:", err);
        setError("Failed to fetch fleets.");
      } finally {
        setLoading(false);
      }
    };

    fetchFleets();
  }, [clientDomain, apiUrl]);

  // Reset selected fleet when the clientDomain changes
  useEffect(() => {
    setSelectedFleet(null);
  }, [clientDomain]);

  const handleFleetAdded = (fleet: {
    id: string;
    name: string;
    vessels: any[];
  }) => {
    setFleets((prev) => [...prev, { ...fleet, clientDomain }]);
  };

  const handleDeleteFleet = async (fleetId: string) => {
    try {
      const apiClient = new ApiClient(apiUrl);
      await apiClient.deleteClientFleet(fleetId);
      setFleets((prev) => prev.filter((f) => f.id !== fleetId));
      if (selectedFleet?.id === fleetId) setSelectedFleet(null);
      setConfirmDelete(null);
    } catch (err) {
      setError("Failed to delete fleet.");
      setConfirmDelete(null);
    }
  };

  if (loading) {
    return <div>Loading fleets...</div>;
  }

  if (error) {
    return <div className="fleet-list-error">{error}</div>;
  }

  return (
    <div className="fleet-list-container">
      <h3 className="fleet-list-title">Fleets for {clientDomain}</h3>

      <table className="fleet-list-table">
        <thead>
          <tr>
            <th>Fleet Name</th>
            <th>Number of Vessels</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {fleets.map((fleet) => (
            <tr
              key={fleet.id}
              className={
                selectedFleet?.id === fleet.id ? "fleet-row-selected" : ""
              }
              onClick={() => setSelectedFleet(fleet)}
            >
              <td>{fleet.name}</td>
              <td>{fleet.vessels.length}</td>
              <td style={{ textAlign: "center" }}>
                <button
                  className="bin-btn"
                  title="Delete Fleet"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (fleet.id) {
                      setConfirmDelete({ id: fleet.id, name: fleet.name });
                    }
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <BinIcon color="black" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <AddFleet
        apiUrl={apiUrl}
        onFleetAdded={handleFleetAdded}
        clientDomain={clientDomain}
      />

      {selectedFleet && selectedFleet.id && (
        <VesselList
          fleetName={selectedFleet.name}
          clientFleetId={selectedFleet.id}
          vessels={selectedFleet.vessels}
        />
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-message">
              Are you sure you want to delete the fleet{" "}
              <b>{confirmDelete.name}</b>?
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn confirm"
                onClick={() => handleDeleteFleet(confirmDelete.id)}
              >
                Yes
              </button>
              <button
                className="modal-btn cancel"
                onClick={() => setConfirmDelete(null)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetList;
